import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { auditApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { CircleNotch, ClockCounterClockwise, User, Package, QrCode, ShieldCheck, Buildings, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';

const ENTITY_ICONS = {
  product: Package,
  qr: QrCode,
  user: User,
  role: ShieldCheck,
  organization: Buildings,
  department: Buildings,
  reprint_request: ClockCounterClockwise,
};

const ACTION_COLORS = {
  create: 'text-emerald-500 bg-emerald-500/10',
  update: 'text-brand-500 bg-brand-500/10',
  delete: 'text-red-500 bg-red-500/10',
  generate: 'text-violet-500 bg-violet-500/10',
  print: 'text-amber-500 bg-amber-500/10',
  reprint: 'text-brand-500 bg-brand-500/10',
  bulk_update: 'text-cyan-500 bg-cyan-500/10',
  review_reprint: 'text-pink-500 bg-pink-500/10',
};

const PAGE_SIZES = [10, 25, 50, 100];

export default function AuditLogPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const loadLogs = useCallback(async () => {
    try {
      const params = {};
      const res = await auditApi.list(params);
      setLogs(res.data);
    } catch { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const filtered = useMemo(() => {
    return logs.filter((log) => {
      if (filterEntity !== 'all' && log.entity_type !== filterEntity) return false;
      if (filterAction !== 'all' && log.action !== filterAction) return false;
      return true;
    });
  }, [logs, filterEntity, filterAction]);

  useEffect(() => {
    setPage(1);
  }, [filterEntity, filterAction, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const formatTime = (ts) => formatDateTime(ts);

  const entityTypes = [...new Set(logs.map((l) => l.entity_type))];
  const actionTypes = [...new Set(logs.map((l) => l.action))];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-log-page">
        <PageHeader
          title={t('audit.title')}
          description={`${filtered.length} ${t('audit.events')}`}
        />

        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{t('audit.entity', { defaultValue: 'Entity' })}</Label>
                <Select value={filterEntity} onValueChange={setFilterEntity}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-entity">
                    <SelectValue placeholder="All Entities" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    <SelectItem value="all">{t('audit.allEntities')}</SelectItem>
                    {entityTypes.map((entity) => (
                      <SelectItem key={entity} value={entity}>{entity}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{t('audit.eventType', { defaultValue: 'Event Type' })}</Label>
                <Select value={filterAction} onValueChange={setFilterAction}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-action">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    <SelectItem value="all">{t('audit.allActions')}</SelectItem>
                    {actionTypes.map((action) => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{t('audit.perPage', { defaultValue: 'Per page' })}</Label>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="audit-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    {PAGE_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className={`text-sm flex items-end ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                Showing {filtered.length === 0 ? 0 : (pageSafe - 1) * pageSize + 1}–
                {Math.min(pageSafe * pageSize, filtered.length)} of {filtered.length}
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch className="w-8 h-8 animate-spin text-brand-500" /></div>
        ) : filtered.length === 0 ? (
          <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
            <CardContent className="py-16 text-center">
              <ClockCounterClockwise className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <p className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('audit.noLogs')}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{t('audit.logsWillAppear')}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardContent className="p-0">
              <div className="divide-y divide-zinc-800/50">
                {pageRows.map((log, i) => {
                  const Icon = ENTITY_ICONS[log.entity_type] || ClockCounterClockwise;
                  const colorClass = ACTION_COLORS[log.action] || 'text-zinc-500 bg-zinc-500/10';
                  return (
                    <div key={log.id || i} className={`flex items-start gap-3 px-4 py-3 ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-zinc-50'} transition-colors`}
                      data-testid={`audit-log-${i}`}>
                      <div className={`shrink-0 mt-0.5 p-1.5 rounded-lg ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
                            {t(`audit.actions.${log.action}`, { defaultValue: log.action })}
                          </span>
                          <span className={`text-xs font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                            {t(`audit.entities.${log.entity_type}`, { defaultValue: log.entity_type })}
                          </span>
                        </div>
                        {log.details && (
                          <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{log.details}</p>
                        )}
                        <div className={`flex items-center gap-2 mt-1 text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                          <span>{log.user_name || 'System'}</span>
                          <span>&middot;</span>
                          <span>{formatTime(log.timestamp)}</span>
                          {log.entity_id && <><span>&middot;</span><span className="font-mono">{log.entity_id.substring(0, 12)}</span></>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageSafe <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                >
                  <CaretLeft className="w-4 h-4 mr-1" /> Prev
                </Button>
                <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  Page {pageSafe} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageSafe >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                >
                  Next <CaretRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
